import {
	FileTypeEnum,
	MAPPING_DOC_TYPE,
	PageTypeEnum,
	TableTypes,
	TextMessage,
} from '@/constant';
import { useGetListData } from '@/hooks';
import {
	IAutocompleteSuggestionItem,
	replacementFile,
	replacementForm,
	uploadFile,
} from '@/services';
import {
	CommonActions,
	CommonSelectors,
	useAppDispatch,
	useAppSelector,
} from '@/stores';
import {
	deepCompare,
	filter,
	formatDate,
	isFunction,
	joinMultipleStrings,
	map,
	NOOP,
	removeFileNameInvalidChars,
	showAlert,
} from '@/utils';
import { printDocument } from '@/utils/pdf.util';
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { clone, includes, sum } from 'ramda';

import { SelectFormData } from '../common/autocomplete-form';
import { TriStateValueEnum } from '../common/checkbox-tri-state';
import { DatePickerData } from '../common/date-picker-form';
import { FileInputData } from '../common/input-file-form';
import { RangePickerData } from '../common/range-picker-form';
import { SignatureData } from '../common/signature';
import { TimePickerData } from '../common/time-picker-form';
import { FORM_CONFIG_JSON, REDUX_JSON } from './define-document-write.config';
import {
	getNextStateOfTriStateCheckbox,
	getReferenceData,
} from './define-document-write.helper';
import {
	CheckboxType,
	DefineDocumentWriteModeEnum,
	WrapDocumentProps,
} from './define-document-write.type';

export const useLogic = (props: WrapDocumentProps) => {
	const dispatch = useAppDispatch();
	const {
		documentType,
		rowId,
		onUploaded,
		refTableName,
		title,
		onCloseModal,
		mode,
		refTableId,
		initDataFromIssueApplication,
		fileAuto,
		mappingList,
		hasEmptyErrors,
		dataViewIssueApplicant = [],
		fileId,
	} = props;

	const pageRefs = useRef<Array<HTMLDivElement | null>>([]);
	const { params: updateHistoryParams } = useGetListData({
		type: TableTypes.UPDATE_HISTORY,
	});

	const docType = MAPPING_DOC_TYPE[documentType];

	const detailData: any = useAppSelector(
		// @ts-expect-error: ignore here
		REDUX_JSON[docType] || NOOP
	);

	const jsonContent = useAppSelector(CommonSelectors.selectJsonContent);

	const autocompleteSuggestion = useAppSelector(
		CommonSelectors.selectAutocompleteSuggestion
	);

	const formConfigs = useMemo(() => {
		return FORM_CONFIG_JSON[docType];
	}, [docType]);

	const pages = useMemo(() => {
		const configPages = formConfigs?.pages;
		const resolved = isFunction(configPages)
			? configPages({ refTableName })
			: configPages;
		// Strip the function branch from the type so the view infers the same
		// `PageConfig[]` union as before (keeps `page` strongly typed).
		return resolved as Exclude<
			typeof configPages,
			((...args: any[]) => any) | undefined
		>;
	}, [formConfigs, refTableName]);

	const [initData, setInitData] = useState({
		...(formConfigs?.initializeValue as Record<string, any>),
		...jsonContent,
		...((initDataFromIssueApplication || {}) as Record<string, any>),
	});

	const [data, setData] = useState(initData);

	const [showError, setShowError] = useState(false);

	const [isFileSaved, setIsFileSaved] = useState(false);

	const hasFile = useMemo(() => {
		return !!fileId || isFileSaved;
	}, [fileId, isFileSaved]);

	const fetchData = async () => {
		const referenceData = await getReferenceData({
			docType,
			refTableName,
			refTableId,
			documentType,
			mappingList: props.mappingList,
			detailData,
		});

		const mappedData =
			formConfigs?.mapPropToValues?.({
				docType,
				refTableName,
				detailData,
				documentType,
				...referenceData,
			} as any) || {};

		setData((prev) => ({
			...prev,
			...mappedData,
		}));

		setInitData((prev) => ({
			...prev,
			...mappedData,
		}));
	};

	useEffect(() => {
		if (!open) {
			setData(initData);
			return;
		}
		if (mode === 'CREATE') {
			fetchData();
		}
	}, [open]);

	const hasSubmitted = useRef(false);
	useEffect(() => {
		if (mode === 'AUTO' && pageRefs.current.length > 0) {
			// check all images loaded
			const checkImagesLoaded = () => {
				if (hasSubmitted.current) return;
				const allImages = pageRefs.current.reduce<HTMLImageElement[]>(
					(acc, ref) => {
						if (ref) {
							const images = ref.getElementsByTagName('img');
							return [...acc, ...Array.from(images)];
						}
						return acc;
					},
					[]
				);

				// if no images, submit
				if (allImages.length === 0) {
					hasSubmitted.current = true;

					onSubmit();
					return;
				}

				// count loaded images
				let loadedImages = 0;
				const totalImages = allImages.length;

				const imageLoaded = () => {
					loadedImages++;

					if (loadedImages === totalImages) {
						hasSubmitted.current = true;
						// Wait one frame to ensure rendering is complete
						requestAnimationFrame(() => {
							onSubmit();
						});
					}
				};

				// Check each image
				allImages.forEach((img) => {
					if (img.complete) {
						imageLoaded();
					} else {
						img.addEventListener('load', imageLoaded);
						img.addEventListener('error', imageLoaded); // Treat as loaded even on error
					}
				});
			};

			// Wait 1s before checking
			setTimeout(checkImagesLoaded, 1000);
		}

		return () => {
			hasSubmitted.current = false;
		};
	}, [mode]);

	useEffect(() => {
		if (mode !== DefineDocumentWriteModeEnum.AUTO) {
			dispatch(CommonActions.getAutocompleteSuggestionRequest());
		}

		return () => {
			dispatch(CommonActions.resetDocumentJsonContent());
		};
	}, [mode]);

	const getAutocompleteOptions = useCallback(
		(refTableName?: PageTypeEnum) => {
			let list: IAutocompleteSuggestionItem[] = [];

			switch (refTableName) {
				case PageTypeEnum.FRANCHISE:
					list = autocompleteSuggestion?.franchises || [];
					break;

				case PageTypeEnum.APPLICANT:
					list = autocompleteSuggestion?.applicants || [];
					break;

				case PageTypeEnum.RECOMMENDER:
					list = autocompleteSuggestion?.recommenders || [];
					break;

				case PageTypeEnum.LODGING:
					list = autocompleteSuggestion?.lodgings || [];
					break;

				default:
					list = [];
			}
			return map(list, (item) => ({ value: item?.id, label: item?.value }));
		},
		[autocompleteSuggestion]
	);

	const fileName = useMemo(() => {
		const subjectName = getAutocompleteOptions(refTableName)?.find(
			(item) => item.value === refTableId
		)?.label;

		return joinMultipleStrings([
			removeFileNameInvalidChars(subjectName || '', { symbol: '_' }),
			removeFileNameInvalidChars(title, { symbol: '_' }),
			mode !== DefineDocumentWriteModeEnum.AUTO
				? `${formatDate(new Date(), {
						outputFormat: 'YYYYMMDDHHmm',
					})}.pdf`
				: '',
		]);
	}, [title, getAutocompleteOptions, refTableName, refTableId]);

	const onCancel = useCallback(() => {
		if (!deepCompare(initData, data)) {
			showAlert(TextMessage.ALERT_20, onCloseModal, NOOP);
			return;
		}

		onCloseModal();
	}, [initData, data, onCloseModal]);

	const createFormDataForReplace = (filePdf: File) => {
		const formData = new FormData();
		formData.append('mutipartFile', filePdf);
		formData.append('fileType', FileTypeEnum.FORM);
		formData.append('documentType', documentType);
		formData.append('id', String(rowId));
		formData.append('jsonContent', JSON.stringify(data));
		if (refTableName) {
			formData.append('refTableName', refTableName as string);
		}
		if (refTableId) {
			formData.append('refTableId', String(refTableId));
		}
		return formData;
	};

	const createFormDataForIssueApplicationDownload = (filePdf: File) => {
		const formData = new FormData();
		formData.append('reqFile.mutipartFile', filePdf);
		formData.append('reqFile.id', String(rowId));
		formData.append('reqFile.jsonContent', JSON.stringify(data));
		formData.append('reqFile.refTableName', refTableName as string);
		formData.append('reqFile.refTableId', String(refTableId));
		props.mappingList?.forEach((item, index) => {
			if (item.id) {
				formData.append(`reqReferList[${index}].id`, String(item.id));
			}
			formData.append(`reqReferList[${index}].refTableName`, item.refTableName);
			formData.append(
				`reqReferList[${index}].refTableId`,
				String(item.refTableId)
			);
		});

		return formData;
	};

	const createFormDataForUpload = (filePdf: File) => {
		const formData = new FormData();
		formData.append('reqUploadDocumentList[0].mutipartFile', filePdf);
		formData.append('reqUploadDocumentList[0].fileType', FileTypeEnum.FORM);
		formData.append('reqUploadDocumentList[0].documentType', documentType);
		formData.append(
			'reqUploadDocumentList[0].jsonContent',
			JSON.stringify(data)
		);
		return formData;
	};

	const handleSuccess = (fileUploaded?: any, hasCloseModal = true) => {
		switch (mode) {
			case DefineDocumentWriteModeEnum.CREATE:
			case DefineDocumentWriteModeEnum.UPDATE:
				onUploaded({ rowId, fileUploaded, mode });
				break;
			case DefineDocumentWriteModeEnum.REPLACE:
			case DefineDocumentWriteModeEnum.ISSUE_APPLICATION_DOWNLOAD:
				onUploaded?.();
				if (refTableId && refTableName) {
					dispatch(
						CommonActions.getUpdateHistoryListRequest({
							...updateHistoryParams,
							page: 1,
							refTableId: refTableId as number,
							refTableName: refTableName as any,
							allData: false,
						})
					);
				}
				break;
			case DefineDocumentWriteModeEnum.AUTO:
				onUploaded?.({ ...fileUploaded });
				break;
		}

		setIsFileSaved(true);

		if (hasCloseModal) {
			onCloseModal();
		} else {
			setInitData(data);
		}
	};

	const handleValidationAndProcessDocument = async (
		callback?: (data?: any) => void,
		hasCloseModal?: boolean
	) => {
		// Validate form if needed
		if (formConfigs?.validateOnSave) {
			const result = formConfigs.validateOnSave(data as any);
			if (result.hasError) {
				showAlert(
					result.message || TextMessage.ERROR_MESSAGE_REQUIRED_FIELD,
					() => {
						setShowError(true);
						callback?.({
							isError: true,
							message:
								result.message || TextMessage.ERROR_MESSAGE_REQUIRED_FIELD,
						});
					}
				);
				return false;
			}
		}

		// Generate PDF document
		const filePdf = await printDocument(pageRefs.current, fileName);

		// Handle issue application download with fileAuto
		if (
			mode === DefineDocumentWriteModeEnum.ISSUE_APPLICATION_DOWNLOAD &&
			fileAuto
		) {
			const formData = createFormDataForIssueApplicationDownload(filePdf);
			const res = await replacementForm(formData);

			callback?.();
			handleSuccess(res?.data?.[0], hasCloseModal);
			return true;
		}

		// Handle replacement modes
		if (
			includes(mode, [
				DefineDocumentWriteModeEnum.REPLACE,
				DefineDocumentWriteModeEnum.ISSUE_APPLICATION_DOWNLOAD,
			])
		) {
			const formData = createFormDataForReplace(filePdf);
			await replacementFile(formData);
			callback?.();
			handleSuccess(undefined, hasCloseModal);
			return true;
		}

		// Handle upload mode (default)
		const formData = createFormDataForUpload(filePdf);
		const res = await uploadFile(formData);
		callback?.();
		handleSuccess(res?.data?.[0], hasCloseModal);
		return true;
	};

	const onSubmit = useCallback(
		async (callback?: (data?: any) => void, hasCloseModal?: boolean) => {
			if (!pageRefs?.current.length) {
				return;
			}

			const isAutoIssueApplication =
				dataViewIssueApplicant.some(
					(item) =>
						item?.documentType === documentType &&
						Number(item?.id) !== Number(rowId)
				) && fileAuto === true;

			// Special case: Subject with ISSUE_APPLICATION_DOWNLOAD mode
			if (
				(!fileAuto &&
					mode === DefineDocumentWriteModeEnum.ISSUE_APPLICATION_DOWNLOAD) ||
				isAutoIssueApplication
			) {
				showAlert(
					TextMessage.ALERT_87,
					async () => {
						try {
							await handleValidationAndProcessDocument(callback, hasCloseModal);
						} catch (error) {
							console.error('Error during file upload:', error);
							callback?.();
						}
					},
					() => callback?.(),
					{
						cancelText: '취소',
						confirmText: '저장',
					}
				);
				return;
			}

			// Normal flow
			try {
				await handleValidationAndProcessDocument(callback, hasCloseModal);
			} catch (error) {
				console.error('Error during file upload:', error);
				callback?.();
			}
		},
		[
			mode,
			mappingList,
			handleValidationAndProcessDocument,
			dataViewIssueApplicant,
			documentType,
			fileAuto,
			mode,
		]
	);

	// Apply the derived-field rules declared by the config.
	// Generic: the shared layer does not know any specific field name - it only
	// runs rules whose `when` includes the field that just changed. Supports
	// two-way relationships.
	const applyDerivedFields = useCallback(
		(next: Record<string, any>, changedName: string) => {
			formConfigs?.derivedFields?.forEach((rule) => {
				if (rule.when.includes(changedName)) {
					next[rule.set] = rule.compute(next);
				}
			});
			return next;
		},
		[formConfigs]
	);

	const onChangeInput = useCallback(
		(e: React.ChangeEvent<HTMLTextAreaElement>) => {
			setData((prevData) => {
				const next: Record<string, any> = {
					...prevData,
					[e.target.name]: e.target.value,
				};
				return applyDerivedFields(next, e.target.name);
			});
		},
		[applyDerivedFields]
	);

	const onChangeInputJson = useCallback(
		(jsonKey: string, resetKeys?: string[]) =>
			(
				e:
					| React.ChangeEvent<HTMLTextAreaElement>
					| React.ChangeEvent<HTMLSelectElement>
			) => {
				const index = Number(e.target.dataset?.tabindex);
				const name = e.target.name;

				const cloneData = clone(data[jsonKey]);
				if (cloneData) {
					cloneData[index][name] = e.target.value;
					// total registered area and manpower
					if (
						jsonKey === 'attractionPlanAgricultural' ||
						jsonKey === 'attractionPlanFishery'
					) {
						const isCheckNilValue = cloneData
							?.slice(0, cloneData?.length - 1)
							?.every((item: any) => !item[name]);
						cloneData[cloneData?.length - 1][name] = isCheckNilValue
							? ''
							: sum(
									cloneData
										?.slice(0, cloneData?.length - 1)
										?.map((item: any) => Number(item[name]))
								);
					}
					map(resetKeys, (key) => {
						cloneData[index][key] = '';
					});

					setData((prev) => ({
						...prev,
						[jsonKey]: cloneData,
					}));
				}
			},
		[data]
	);

	const onClickCheckbox = useCallback(
		(checked: boolean, type?: CheckboxType, getResetKey?: any) =>
			(e: React.MouseEvent<HTMLElement>) => {
				const isMultiple = type === CheckboxType.MULTIPLE;

				const value = e.currentTarget.dataset.value || '';
				const accessKey = e.currentTarget?.accessKey;

				if (!accessKey) return;

				const resetKeys: string[] = getResetKey?.(value) || [];

				const resetJson: Record<string, any> = {};

				resetKeys?.forEach((key) => {
					//@ts-expect-error: ignore here
					resetJson[key] = formConfigs?.initializeValue?.[key] || '';
				});

				setData((prevData) => {
					const next = {
						...prevData,
						[accessKey]: isMultiple
							? checked
								? filter(
										prevData[accessKey],
										(item: typeof value) => item !== value
									)
								: prevData?.[accessKey]?.concat(value)
							: (prevData?.[accessKey] !== value && value) || '',
						...resetJson,
					};
					return applyDerivedFields(next, accessKey);
				});
			},
		[applyDerivedFields]
	);

	const onClickCheckboxAll = useCallback(
		(checked: boolean, options: any[], getResetKey?: any) =>
			(e: React.MouseEvent<HTMLElement>) => {
				const accessKey = e.currentTarget?.accessKey;

				if (!accessKey) return;

				const resetKeys: string[] = getResetKey?.(checked) || [];

				const resetJson: Record<string, any> = {};

				resetKeys?.forEach((key) => {
					resetJson[key] = '';
				});

				setData((prevData) => {
					return {
						...prevData,
						[accessKey]: checked ? [] : options || [],
						...resetJson,
					};
				});
			},
		[]
	);

	const onClickTriStateCheckbox = useCallback(
		(index: number) => (e: React.MouseEvent<HTMLElement>) => {
			const accessKey = e.currentTarget?.accessKey;
			if (!accessKey) return;

			const currentValue =
				(e.currentTarget.dataset.value as TriStateValueEnum) ||
				TriStateValueEnum.EMPTY;
			const newValue = getNextStateOfTriStateCheckbox(currentValue);

			setData((prevData) => {
				const currentArray = prevData[accessKey];
				if (!Array.isArray(currentArray)) return prevData; // Protection if data is invalid

				const updatedArray = [...currentArray];
				updatedArray[index] = newValue;

				return {
					...prevData,
					[accessKey]: updatedArray,
				};
			});
		},
		[]
	);

	const onSubmitSignature = useCallback((signatureData: SignatureData) => {
		setData((prev) => ({
			...prev,
			[signatureData?.name]: signatureData?.imgData,
		}));
	}, []);

	const onSubmitSignatureJson = useCallback(
		(jsonKey: string, valueKey: string, index: number) =>
			(signatureData: SignatureData) => {
				const cloneData = clone(data[jsonKey]);

				if (cloneData) {
					cloneData[index][valueKey] = signatureData?.imgData;

					setData((prev) => ({
						...prev,
						[jsonKey]: cloneData,
					}));
				}
			},
		[data]
	);

	const onChangeDate = useCallback(
		(datePickerData: DatePickerData) => {
			setData((prev) => {
				const next: Record<string, any> = {
					...prev,
					[datePickerData?.name]: formatDate(datePickerData?.date, {
						outputFormat: datePickerData?.outputFormat,
					}),
				};
				return applyDerivedFields(next, datePickerData?.name);
			});
		},
		[applyDerivedFields]
	);

	const onChangeDateJson = useCallback(
		(jsonKey: string, valueKey: string, index: number) =>
			(datePickerData: DatePickerData) => {
				const cloneData = clone(data[jsonKey]);

				if (cloneData) {
					cloneData[index][valueKey] = formatDate(datePickerData?.date, {
						outputFormat: datePickerData?.outputFormat,
					});

					setData((prev) => ({
						...prev,
						[jsonKey]: cloneData,
					}));
				}
			},
		[data]
	);

	const onChangeInputFile = useCallback((fileInputData: FileInputData) => {
		setData((prev) => ({
			...prev,
			[fileInputData?.name]: fileInputData?.imgData,
		}));
	}, []);

	const onChangeRangePicker = useCallback(
		(rangePickerData: RangePickerData) => {
			setData((prev) => ({
				...prev,
				[rangePickerData?.name?.[0]]: rangePickerData?.dateStrings?.[0],
				[rangePickerData?.name?.[1]]: rangePickerData?.dateStrings?.[1],
			}));
		},
		[]
	);

	const onChangeTimePicker = useCallback((timePickerData: TimePickerData) => {
		setData((prev) => ({
			...prev,
			[timePickerData?.name]: timePickerData?.dateString,
		}));
	}, []);

	const onChangeAutocompleteJson = useCallback(
		(jsonKey: string, resetKeys?: string[]) =>
			(selectFormData: SelectFormData) => {
				const { index = 0, value, name } = selectFormData;

				const cloneData = clone(data[jsonKey]);

				if (cloneData) {
					cloneData[index][name] = value;

					setData((prev) => ({
						...prev,
						[jsonKey]: cloneData,
					}));
				}
			},
		[data]
	);

	return {
		title,
		fileName,
		formConfigs,
		pages,
		pageRefs,
		data,
		showError,
		hasEmptyErrors,
		hasFile,
		handlers: {
			onSubmit,
			setData,
			onChangeInput,
			onClickCheckbox,
			onSubmitSignature,
			onSubmitSignatureJson,
			onChangeDate,
			onChangeInputJson,
			onChangeDateJson,
			onClickCheckboxAll,
			onChangeInputFile,
			onChangeRangePicker,
			onChangeTimePicker,
			onChangeAutocompleteJson,
			onCancel,
			onClickTriStateCheckbox,
		},
	};
};
